import { createContext, useContext, useRef, useState } from 'react'
import { ContentPlanningContextValue, ContentPlanningProviderProps } from './ContentPlanningContextTypes'
import { postLengthToNumber } from '../ContentPlanning/utils'
import { useSourceMaterials } from 'hooks/useSourceMaterials'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation } from 'react-query'
import { generateSocialCaptions } from '../generateSocialCaptions'
import { ourUuid } from 'utils'
import { GeneratedContent } from '../ContentPlanning/SelectContent/SelectContent'
import { ScheduledContent } from '../ContentPlanning/ScheduleContent/ScheduleContent'
import { useModals } from 'almanac-ui/Modal'
import { AiErrorModal } from 'components/AiErrorModal/AiErrorModal'
import { useCreateCardDismissal } from 'hooks/queries/useCreateCardDismissal'
import {
  CreateCampaignPayload,
  GenerateCampaignDocsPayload,
  createCampaignRequest,
  generateDocsRequest,
} from 'components/CampaignCreationModal/createRequest'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { AiCampaignCreationError } from 'components/CampaignCreationModal/CampaignCreationModal'
import { ChannelOption } from '../CampaignWizard/Types'
import { CampaignType } from 'components/CampaignCreationModal/Types'
import { AlmanacFile, ViewType } from 'types'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { DOC_PATH } from 'paths'
import { Tone } from 'components/VoiceAndTonesSelect/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import dayjs from 'dayjs'

interface Values {
  topic?: string
  caption_length?: string
  caption_count?: string
  filename?: string
  brand_voice_id?: number
  tone?: Array<Tone>
  language?: string
  project?: AlmanacFile
  source_material_ids?: number[]
  promotion?: string
  call_to_action?: string
  author_persona?: string
  product_description?: string
  avoid?: string
  additional_info?: string
  include_images?: boolean
  external_integration_id?: number
  apply_brand?: boolean
  brand_visual_kit_id?: number
  brand_kit_id?: number
}

const ContentPlanningContext = createContext<ContentPlanningContextValue>({
  generatedCaptions: [],
  selectedCaptions: [],
  scheduledCaptions: [],
  setGeneratedCaptions: () => {},
  setSelectedCaptions: () => {},
  setScheduledCaptions: () => {},
  generateCaptions: () => {},
  generateCampaign: () => {},
  generationType: DesignChannel.InstagramPost,
})

export const ContentPlanningContextProvider = ContentPlanningContext.Provider

export const ContentPlanningProvider = ({
  children,
  captionsGenerationType,
  generationType,
  initialValues,
}: ContentPlanningProviderProps) => {
  const { openModal, closeModal, closeAllModals } = useModals()
  const sourceMaterials = useSourceMaterials()
  const workspaceId = useCurrentWorkspaceId()
  const { mutate: dismissCard } = useCreateCardDismissal()
  const { currentFolder } = useCurrentFolderStore()
  const onDoc = useRouteMatch(DOC_PATH)
  const history = useHistory()

  const generateCaptionsMutationAbortController = useRef<AbortController | null>(null)
  if (generateCaptionsMutationAbortController.current === null)
    generateCaptionsMutationAbortController.current = new AbortController()
  const generateCaptionsMutation = useMutation(generateSocialCaptions)

  const initialScheduledCaptions = (initialValues?.scheduled_captions || []).map((c: ScheduledContent) => ({
    ...c,
    postAt: dayjs(c.postAt),
  }))

  const [generatedCaptions, setGeneratedCaptions] = useState<GeneratedContent[]>(
    initialValues?.generated_captions || [],
  )
  const [selectedCaptions, setSelectedCaptions] = useState<string[]>(initialValues?.selected_captions || [])
  const [scheduledCaptions, setScheduledCaptions] = useState<ScheduledContent[]>(initialScheduledCaptions)

  const generateCaptions = async (values: Values, attempts: number) => {
    const caption_length = postLengthToNumber(values?.caption_length)
    const referenceText = (sourceMaterials.data || [])
      .filter(({ id }) => values.source_material_ids?.includes(id))
      .map(({ content }) => content)
      .join('\n')

    const prompt = {
      ...values,
      caption_count: 5,
      caption_length,
      generation_type: captionsGenerationType,
      reference_text: referenceText.length > 0 ? referenceText : undefined,
    }
    const payload = { prompt, workspaceId }
    const signal = generateCaptionsMutationAbortController.current?.signal

    generateCaptionsMutation.mutate(
      {
        payload,
        signal,
      },
      {
        onSuccess(data) {
          const captions = data.map((c) => ({ uuid: ourUuid(), text: c }))
          setGeneratedCaptions((existingCaptions) => [...existingCaptions, ...captions])
          if (attempts > 0) {
            generateCaptions(values, attempts - 1)
          }
        },
        onError(error) {
          reportError(error)

          openModal(AiErrorModal, {
            onCancel: () => {
              closeAllModals()
            },
            onTryAgain: () => {
              closeModal()
            },
          })
        },
      },
    )
  }

  const generateCampaign = async (values: Values) => {
    generateCampaignMutation.mutate(
      {
        workspaceId,
        name: values.filename,
        focus: values.topic,
        brand_voice_id: values.brand_voice_id,
        tones: values.tone,
        project_key: values.project?.id,
      },
      {
        onError: (error) => {
          openModal(AiErrorModal, {
            onCancel: closeModal,
            onTryAgain: () => {
              closeModal()
              generateCampaign(values)
            },
            error,
          })
        },
        onSuccess: ({ content_campaign }) => {
          dismissCard('home_generate_content_cta')
          generateDocs(content_campaign.id, values)
        },
      },
    )
  }

  const generateCampaignMutation = useMutation(async (payload: CreateCampaignPayload) => {
    const response = await createCampaignRequest(payload)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiCampaignCreationError(
        'There was a problem generating the campaign',
        { errorCode: 'campaign' },
        {
          responseData: response.data,
          request: payload,
        },
      )
    }
    return response.data
  })

  const generateDocsMutation = useMutation(async (payload: GenerateCampaignDocsPayload) => {
    const response = await generateDocsRequest(payload)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiCampaignCreationError(
        `There was a problem generating the campaign docs: ${response.data}`,
        { errorCode: 'campaign' },
        {
          responseData: response.data,
          request: {
            workspaceId,
            createCampaignResponseDataContentCampaignId: payload.campaign_id,
          },
        },
      )
    }
    return response.data
  })

  const generateDocs = (campaignId: number, values: Values) => {
    if (!scheduledCaptions.length) return

    const channels: Array<ChannelOption> = scheduledCaptions
      .filter((caption) => caption.status !== 'failed')
      .map((caption) => {
        return {
          channel_type: generationType,
          prompt_library: 'default',
          pregenerated_content: caption.text,
          post_at: caption.postAt?.toISOString(),
          source_visual_element_id: caption.sourceVisualElementId,
          initial_media_file_id: caption.initialMediaFileId,
          apply_brand: false,
          brand_visual_kit_id: values.brand_visual_kit_id,
          topic: values.topic,
          tone: values.tone,
          brand_voice_id: values.brand_voice_id,
          language: values.language,
          source_material_ids: values.source_material_ids,
          brand_kit_id: values.brand_kit_id,
          content: caption.content,
          archetype: caption.archetype,
        }
      })

    generateDocsMutation.mutate(
      {
        workspaceId,
        campaign_id: campaignId,
        campaign_type: CampaignType.Planning,
        channels: channels,
        external_integration_id: values.external_integration_id,
        redirect_to_view: ViewType.Calendar,
        prompt: {
          topic: values.topic,
          brand_voice_id: values.brand_voice_id,
          tone: values.tone,
          language: values.language,
          call_to_action: values.call_to_action,
          author_persona: values.author_persona,
          product_description: values.product_description,
          avoid: values.avoid,
          additional_info: values.additional_info,
          include_images: values.include_images,
          promotion: values.promotion,
          brand_kit_id: values.brand_kit_id,
        },
      },
      {
        onError: (error) => {
          openModal(AiErrorModal, {
            onCancel: closeModal,
            onTryAgain: () => {
              closeModal()
              generateDocs(campaignId, values)
            },
            error,
          })
        },
        onSuccess: ({ almanac_file }) => {
          if (almanac_file.url && (almanac_file.url !== currentFolder?.url || onDoc)) history.push(almanac_file.url)
          closeAllModals()
        },
      },
    )
  }

  return (
    <ContentPlanningContextProvider
      value={{
        generatedCaptions,
        selectedCaptions,
        scheduledCaptions,
        setGeneratedCaptions,
        setSelectedCaptions,
        setScheduledCaptions,
        generateCaptions,
        generateCaptionsMutation,
        generateCampaign,
        generationType,
      }}
    >
      {children}
    </ContentPlanningContextProvider>
  )
}

export const useContentPlanningContext = () => {
  return useContext(ContentPlanningContext)
}
